import axios from 'axios';

import { RECOMMENDED_VENDORS_LENGTH, RECOMMENDED_VENDORS_POOL_SIZE } from '~/constants';
import type { MarketplaceVendorsQuery, RecommendedVendorsQuery, VendorRatingsQuery } from '~/gql/graphql';
import { getQueryString } from '~/utils/formatter';
import { pickRandomVendors } from '~/utils/vendors';

type MarketplaceVendors = MarketplaceVendorsQuery['marketplaceVendors'];
export type RecommendedVendor = MarketplaceVendors['vendors'][number];

type RecommendedVendorPool = RecommendedVendorsQuery['marketplaceVendors'];
type VendorRating = VendorRatingsQuery['marketplaceVendors']['vendors'][number]['rating'];
// The /vendor-ratings endpoint trims its response to just these fields.
type VendorRatingResult = Pick<VendorRatingsQuery['marketplaceVendors']['vendors'][number], 'publicId' | 'rating'>;

// Lean pre-marketplace vendor: the ratings-free pool shape with a rating merged in for the
// narrowed, displayed set only.
export type RecommendedVendorWithRating = RecommendedVendorPool['vendors'][number] & {
  rating?: VendorRating;
};

export type FetchRecommendedVendorsWithRatingsResult = {
  vendors: RecommendedVendorWithRating[];
  count: number;
};

export type FetchRecommendedVendorsParams = {
  postcode?: string;
  categories?: string[];
  promotions?: string[];
  brands?: string[];
  channel?: string | null;
  leadId?: string | null;
};

export type FetchRecommendedVendorsResult = {
  vendors: RecommendedVendor[];
  // Total vendors matching the filter (postcode + categories), independent of the pool `take`.
  // Used to decide whether the full marketplace has more to offer than the recommended set.
  count: number;
};

/**
 * Ranked+random recommended vendors. Reuses the marketplace-vendors endpoint with
 * the same variables, but pins the sort to RANKING and widens the result to the
 * top RECOMMENDED_VENDORS_POOL_SIZE, then randomly narrows (shuffles) to
 * RECOMMENDED_VENDORS_LENGTH so leads are spread across high-ranking vendors
 * rather than always the literal top 3 (INT-1041).
 */
export const fetchRecommendedVendors = async (
  params: FetchRecommendedVendorsParams,
): Promise<FetchRecommendedVendorsResult> => {
  const { postcode, categories, promotions, brands, channel, leadId } = params;

  const queryString = getQueryString([
    { name: 'leadId', value: leadId },
    { name: 'postcode', value: postcode },
    { name: 'categories', value: categories?.join(',') },
    { name: 'promotions', value: promotions?.join(',') },
    { name: 'manufacturerBrands', value: brands?.join(',') },
    { name: 'channel', value: channel },
    { name: 'sort', value: 'RANKING' },
    { name: 'take', value: RECOMMENDED_VENDORS_POOL_SIZE },
  ]);

  const { data } = await axios.get<MarketplaceVendors>(`/api/marketplace/marketplace-vendors${queryString}`);

  return {
    vendors: pickRandomVendors(data?.vendors ?? [], RECOMMENDED_VENDORS_LENGTH),
    count: data?.count ?? 0,
  };
};

/**
 * Pre-marketplace recommended vendors, ratings-optimised.
 *
 * 1. Fetches the top-ranked pool from the lean, ratings-free `/recommended-vendors` endpoint
 *    (zero Google Place Details calls for the pool).
 * 2. Narrows the pool to RECOMMENDED_VENDORS_LENGTH client-side (unchanged selection logic).
 * 3. Fetches ratings for ONLY the narrowed set via `/vendor-ratings` (one Place Details call
 *    per displayed vendor) and merges them in.
 *
 * Place Details volume therefore tracks vendors displayed (~3), not vendors queried (~10).
 */
export const fetchRecommendedVendorsWithRatings = async (
  params: FetchRecommendedVendorsParams,
): Promise<FetchRecommendedVendorsWithRatingsResult> => {
  const { postcode, categories, promotions, brands, channel } = params;

  const poolQueryString = getQueryString([
    { name: 'postcode', value: postcode },
    { name: 'categories', value: categories?.join(',') },
    { name: 'promotions', value: promotions?.join(',') },
    { name: 'manufacturerBrands', value: brands?.join(',') },
    { name: 'channel', value: channel },
    { name: 'sort', value: 'RANKING' },
  ]);

  const { data } = await axios.get<RecommendedVendorPool>(`/api/marketplace/recommended-vendors${poolQueryString}`);

  const selected: RecommendedVendorWithRating[] = pickRandomVendors(data?.vendors ?? [], RECOMMENDED_VENDORS_LENGTH);
  const count = data?.count ?? 0;

  if (selected.length === 0) {
    return { vendors: selected, count };
  }

  // Fetch ratings only for the vendors we're about to display.
  const ratingsQueryString = getQueryString([
    { name: 'postcode', value: postcode },
    { name: 'publicIds', value: selected.map((vendor) => vendor.publicId).join(',') },
  ]);

  const { data: ratings } = await axios.get<VendorRatingResult[]>(
    `/api/marketplace/vendor-ratings${ratingsQueryString}`,
  );

  const ratingByPublicId = new Map((ratings ?? []).map((vendor) => [vendor.publicId, vendor.rating]));

  return {
    vendors: selected.map((vendor) => ({ ...vendor, rating: ratingByPublicId.get(vendor.publicId) })),
    count,
  };
};
